import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  CommentAddRequestBody,
  NotificationRecordResponse,
  useMarkStepNotificationsAsSeen,
} from '@/shared/api';
import { ChatMode, EventTypes } from '@/shared/types';
import { off, on } from '@/shared/utils';
import { ConversationMode } from '../model/types';

type UseNotificationsProps = {
  instanceId: string;
  stepId: string;
  postedBy: CommentAddRequestBody['postedBy'];
  chatMode?: ChatMode;
  code?: string;
  activeOrgId?: string;
  setChatOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setConversationMode: React.Dispatch<React.SetStateAction<ConversationMode>>;
  collapseRef: React.RefObject<HTMLDivElement>;
};

export const useInstanceNotifications = ({
  instanceId,
  stepId,
  postedBy,
  chatMode,
  code,
  activeOrgId,
  setChatOpen,
  setConversationMode,
  collapseRef,
}: UseNotificationsProps) => {
  const queryClient = useQueryClient();
  const { mutate: markStepAsSeen } = useMarkStepNotificationsAsSeen();

  const markStepNotificationsAsSeen = useCallback(() => {
    markStepAsSeen(
      {
        pathParams: { instanceId, stepId },
        queryParams: { ...(code ? { code } : {}) },
      },
      {
        onSuccess: () => {
          if (postedBy === 'publicUser') {
            // refetch notifications for this instance
            queryClient.invalidateQueries([
              'publicNotificationsGetPaginated',
              instanceId,
            ]);
            // refetch this instance as this step's prop: hasUnseenComments should be updated
            queryClient.invalidateQueries(['instances', instanceId, { code }]);
            // refetch notifications count for this instance
            queryClient.invalidateQueries([
              'notifications',
              'instance',
              'user',
              instanceId,
              'info',
              { code },
            ]);
          } else {
            // refetch global notifications
            queryClient.invalidateQueries([
              'globalNotificationsGetPaginated',
              activeOrgId,
            ]);
            // refetch global notifications count
            queryClient.invalidateQueries([
              'notifications',
              activeOrgId,
              'info',
            ]);
            // refetch notifications for this instance
            queryClient.invalidateQueries([
              chatMode === ChatMode.Author
                ? 'authorInstanceNotificationsGetPaginated'
                : 'userInstanceNotificationsGetPaginated',
              instanceId,
            ]);
            // refetch notifications count for this instance
            queryClient.invalidateQueries([
              'notifications',
              'instance',
              chatMode === ChatMode.Author ? chatMode : ChatMode.User,
              instanceId,
              'info',
            ]);
            // refetch this instance
            if (chatMode === ChatMode.Author) {
              queryClient.invalidateQueries([
                'procs',
                'author',
                'instances',
                instanceId,
              ]);
            }
            if (chatMode === ChatMode.User) {
              queryClient.invalidateQueries(['instances', instanceId]);
            }
          }
        },
      },
    );
  }, [
    activeOrgId,
    chatMode,
    code,
    instanceId,
    markStepAsSeen,
    postedBy,
    queryClient,
    stepId,
  ]);

  useEffect(() => {
    const handler = (event: {
      detail: { notification: NotificationRecordResponse };
    }) => {
      const notification = event?.detail?.notification;
      if (
        notification &&
        notification.instance?.id === instanceId &&
        notification.instance?.step?.id === stepId
      ) {
        setChatOpen(true);
        setConversationMode(ConversationMode.Agent);
        markStepNotificationsAsSeen();
        collapseRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    };
    on(EventTypes.NOTIFICATIONS_SIDEBAR_ITEM_CLICKED, handler);

    return () => off(EventTypes.NOTIFICATIONS_SIDEBAR_ITEM_CLICKED, handler);
  }, [
    collapseRef,
    instanceId,
    markStepNotificationsAsSeen,
    setChatOpen,
    setConversationMode,
    stepId,
  ]);

  return {
    markStepNotificationsAsSeen,
  };
};
